import { Component, Input, ElementRef, ViewChild, NgZone } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HelpService } from 'src/app/services/help.service';

@Component({
  selector: 'app-help-modal',
  templateUrl: './help-modal.component.html',
  styleUrls: ['./help-modal.component.scss'],
  standalone: false,
})
export class HelpModalComponent {
  @Input() topicId?: string;
  public content: SafeHtml | null = null;
  public title = 'Help';
  @ViewChild('contentContainer', { static: false }) contentContainer?: ElementRef<HTMLElement>;
  public showBack = false;
  public searchQuery = '';
  public searchResults: Array<any> = [];
  private searchTimer: any = null;
  public searching = false;
  private contentClickListener?: (ev: Event) => void;
  public categories: Array<{ category: string; documents: Array<{ id: string; title: string }> }> = [];
  public selectedCategory: string | null = null;
  public selectedCategoryDocs: Array<{ id: string; title: string }> = [];
  public loadingTopic = false;

  constructor(
    private modalCtrl: ModalController,
    private helpService: HelpService,
    private sanitizer: DomSanitizer
  ) {}

  async ionViewWillEnter() {
    // Wait for HelpService to finish loading the TOC so we can render category cards
    try {
      await this.helpService.ready;
    } catch (e) {
      // ignore — ready should always resolve, but protect against unexpected rejects
    }
    if (this.topicId) {
      const topic = this.helpService.getTopicById(this.topicId);
      if (topic) {
        this.title = topic.title || this.title;
        this.showBack = true;
        this.loadingTopic = true;
        this.helpService.fetchTopicHtml(topic.file).subscribe({
          next: html => {
            this.content = this.sanitizer.bypassSecurityTrustHtml(html);
            this.loadingTopic = false;
            setTimeout(() => this.wireLinks(), 0); // ensure links inside topic work
          },
          error: () => {
            this.content = this.sanitizer.bypassSecurityTrustHtml('<p>Unable to load help content.</p>');
            this.loadingTopic = false;
          }
        });
      }
    } else {
      // Prefer the data-driven categories TOC provided by HelpService (populated during init()).
      const cats = this.helpService.listCategories();
      if (cats && cats.length > 0) {
        // Use structured categories for Angular-rendered category cards
        this.categories = cats.slice();
        this.selectedCategory = null;
        this.title = 'Help';
        this.showBack = false;
        // Clear any previously injected HTML
        this.content = null;
        // No need to wire delegated link handlers for the index view since we use Angular bindings
        return;
      }

      // If categories are still not available for some reason, fall back to server index HTML.
      this.helpService.fetchIndexHtml().subscribe({
        next: html => {
          this.content = this.sanitizer.bypassSecurityTrustHtml(html);
          setTimeout(() => this.wireLinks(), 0);
        },
        error: () => {
          // If server index fetch fails, render client-side index as a fallback
          this.renderIndex();
        }
      });
    }
  }

  private renderIndex() {
    const topics = this.helpService.listTopics();
    const html = '<ul>' + topics.map(t=>`<li><a href="#" data-id="${t.id}">${t.title}</a></li>`).join('') + '</ul>';
    this.title = 'Help';
    this.showBack = false;
    this.content = this.sanitizer.bypassSecurityTrustHtml(html);
    setTimeout(()=> this.wireLinks(), 0);
  }

  close() {
    // Remove delegated listener to avoid leaks
    if (this.contentClickListener && this.contentContainer) {
      try {
        this.contentContainer.nativeElement.removeEventListener('click', this.contentClickListener as EventListener);
      } catch (e) {
        // ignore
      }
      this.contentClickListener = undefined;
    }
    this.modalCtrl.dismiss();
  }

  selectCategory(category: string) {
    // find category docs
    const cat = this.categories.find(c => c.category === category);
    this.selectedCategory = category;
    this.selectedCategoryDocs = cat ? (cat.documents || []) : [];
    this.showBack = true;
    // clear any injected index content
    this.content = null;
    console.log('[HelpModal] selectCategory', { category, docCount: this.selectedCategoryDocs.length });
  }

  formatCategoryName(cat: string): string {
    if (!cat) return 'General Help';
    if (cat.toLowerCase() === 'uncategorized') return 'General Help';
    // prettify dashed categories
    return cat.replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  }

  getCategoryIcon(cat: string): string {
    if (!cat) return 'help-circle-outline';
    const key = cat.toLowerCase();
    const map: Record<string, string> = {
      'ai-features': 'bulb-outline',
      'analytics': 'trending-up-outline',
      'content-creation': 'create-outline',
      'getting started': 'rocket-outline',
      'getting-started': 'rocket-outline',
      'platforms': 'share-outline',
      'reference': 'book-outline',
      'scheduling': 'calendar-outline',
      'general help': 'help-circle-outline',
      'uncategorized': 'help-circle-outline'
    };
    return map[key] || 'help-circle-outline';
  }

  private wireLinks() {
    if (!this.contentContainer) return;
    const el = this.contentContainer.nativeElement;

    // Remove previous delegated listener if present
    if (this.contentClickListener) {
      el.removeEventListener('click', this.contentClickListener as EventListener);
      this.contentClickListener = undefined;
    }

    // Delegated click handler for anchors inside the content container.
    this.contentClickListener = (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const a = (target.closest && (target.closest('a') as HTMLAnchorElement)) as HTMLAnchorElement | null;
      if (!a) return;

      const href = a.getAttribute('href') || '';

      // data-id links take precedence
      const dataId = a.getAttribute('data-id');
      if (dataId) {
        ev.preventDefault();
        this.loadTopicById(dataId);
        return;
      }

      // Fragment-style links like #ai-features/overview
      if (href.startsWith('#')) {
        ev.preventDefault();
        const id = href.replace(/^#/, '');
        if (id) this.loadTopicById(id);
        return;
      }

      // Asset links: /assets/help/.../name.html
      try {
        const url = new URL(href, window.location.origin);
        const path = url.pathname;
        const marker = '/assets/help/';
        const idx = path.indexOf(marker);
        if (path.endsWith('.html') && idx !== -1) {
          ev.preventDefault();
          const rel = path.substring(idx + marker.length);
          const id = rel.replace(/\.html$/i, '');
          this.loadTopicById(id);
          return;
        }
      } catch (e) {
        // ignore parsing errors and allow default navigation
      }
    };

    el.addEventListener('click', this.contentClickListener as EventListener);
  }

  public loadTopicById(id: string) {
    this.showBack = true;
    // When loading a specific topic, hide any selected category/list so only the
    // document content is shown in the modal body.
    this.selectedCategory = null;
    this.selectedCategoryDocs = [];
    this.loadingTopic = true;

    // Try to resolve a friendly title from local topics if available
    const topic = this.helpService.getTopicById(id);
    if (topic && topic.title) {
      this.title = topic.title;
    }

    // Clear previous content while fetching the new topic
    this.content = null;

    console.log('[HelpModal] loadTopicById start', { id });
    // Always fetch by id (service will prefer API JSON and fall back to local asset)
    this.helpService.fetchTopicHtmlById(id).subscribe({
      next: html => {
        console.log('[HelpModal] loadTopicById success', { id, length: html ? html.length : 0 });
        this.content = this.sanitizer.bypassSecurityTrustHtml(html);
        this.loadingTopic = false;
        setTimeout(() => this.wireLinks(), 0);
      },
      error: (err) => {
        console.error('[HelpModal] loadTopicById error', { id, err });
        this.content = this.sanitizer.bypassSecurityTrustHtml('<p>Unable to load help content.</p>');
        this.loadingTopic = false;
      }
    });
  }

  onSearchChange() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.doSearch(), 300);
  }

  private doSearch() {
    const q = (this.searchQuery || '').trim();
    if (!q) {
      this.searchResults = [];
      this.searching = false;
      return;
    }
    this.searching = true;
    this.helpService.searchHelp(q).subscribe({
      next: (res: any) => {
        this.searchResults = res.results || res.docs || [];
        this.searching = false;
      },
      error: () => {
        this.searchResults = [];
        this.searching = false;
      }
    });
  }

  selectSearchResult(r: any) {
    // result id maps to doc id (path-like). Use that to load the topic.
    const id = r.id;
    if (!id) return;
    // clear search UI
    this.searchQuery = '';
    this.searchResults = [];
    console.log('[HelpModal] selectSearchResult', { id, title: r.title });
    this.loadTopicById(id);
  }

  public goToIndex() {
    // Return to the category-card landing view if categories are available
    this.topicId = undefined;
    this.loadingTopic = false;
    if (this.categories && this.categories.length) {
      this.selectedCategory = null;
      this.selectedCategoryDocs = [];
      this.content = null;
      this.showBack = false;
      console.log('[HelpModal] goToIndex -> category landing');
      return;
    }

    // Fallback: request server index HTML (which itself will prefer categories if available)
    this.helpService.fetchIndexHtml().subscribe({
      next: html => {
        console.log('[HelpModal] fetchIndexHtml success (fallback)', { length: html ? html.length : 0 });
        this.content = this.sanitizer.bypassSecurityTrustHtml(html);
        setTimeout(() => this.wireLinks(), 0);
      },
      error: (err) => {
        console.warn('[HelpModal] fetchIndexHtml failed (fallback)', { err });
        // Fall back to a client-side index if the API is unavailable
        this.renderIndex();
      }
    });
  }
}
